import { wrapUseRoutesV6 } from '@sentry/react';
import {
  AppRoutePaths,
  AppRouterParams,
  CreditLimitRecalculationRoutePaths,
  CreditLineRoutePaths,
  CreditLineWithdrawalRoutePaths,
  DirectPaymentRoutePaths,
  EPARAKSTS_LOGIN_PAGE_PATH,
  ERROR_PAGE_PATH,
  HirePurchaseRoutePaths,
  IncomeInsuranceRoutePaths,
  ROOT_PAGE_PATH,
  SmallLoanRoutePaths,
  UNKNOWN_PAGE_ROUTE_PATH,
} from 'app-constants';
import { DefaultLoader } from 'components/default-loader/DefaultLoader';
import { PageUrlCheckRender } from 'components/page-url-check-render';
import { useShouldShowSchedulePage } from 'hooks';
import { IncomeInsuranceLoginPage } from 'pages/income-insurance/login';
import { memo } from 'react';
import { useRoutes } from 'react-router-dom';

import { AuthInsuranceHashPageUrlCheckRender } from './AuthInsuranceHashPageUrlCheckRender';
import { AuthRedirect } from './AuthRedirect';
import { CreditLineRouteWithPageUrlCheck } from './CreditLineRouteWithPageUrlCheck';
import pages from './lazy-pages';
import { PageUrlCheckedRoute } from './PageUrlCheckedRoute';
import { Redirect } from './Redirect';

const {
  // Root pages
  CreditLineRootPage,
  CreditLineWithdrawalRootPage,
  IncomeInsuranceRootPage,
  SmallLoanRootPage,
  HirePurchaseRootPage,
  CreditLimitRecalculationRootPage,
  // Schedule pages
  HirePurchaseSchedulePage,
  // Checkout pages
  HirePurchaseCheckoutPage,
  SmallLoanCheckoutPage,
  CreditLineWithdrawalCheckoutPage,
  CreditLimitRecalculationCheckoutPage,
  CreditLineCheckoutPage,
  // Login pages
  HirePurchaseLoginPage,
  SmallLoanLoginPage,
  CreditLineLoginPage,
  CreditLineWithdrawalLoginPage,
  CreditLimitRecalculationLoginPage,
  // Contact pages
  HirePurchaseContactPage,
  SmallLoanContactPage,
  CreditLineContactPage,
  CreditLimitRecalculationContactPage,
  CreditLineWithdrawalContactPage,
  // Purpose of loan pages
  HirePurchasePurposeOfLoanPage,
  SmallLoanPurposeOfLoanPage,
  CreditLinePurposeOfLoanPage,
  CreditLimitRecalculationPurposeOfLoanPage,
  CreditLineWithdrawalPurposeOfLoanPage,
  // Contact extra pages
  HirePurchaseContactExtraPage,
  SmallLoanContactExtraPage,
  CreditLineContactExtraPage,
  CreditLimitRecalculationContactExtraPage,
  CreditLineWithdrawalContactExtraPage,
  // Account scoring pages
  HirePurchaseAccountScoringPage,
  SmallLoanAccountScoringPage,
  CreditLineAccountScoringPage,
  CreditLimitRecalculationAccountScoringPage,
  CreditLineWithdrawalAccountScoringPage,
  // EMTA consent pages
  HirePurchaseEmtaConsentPage,
  SmallLoanEmtaConsentPage,
  CreditLineEmtaConsentPage,
  CreditLimitRecalculationEmtaConsentPage,
  CreditLineWithdrawalEmtaConsentPage,
  // Income verification pages
  CreditLineIncomeVerificationPage,
  CreditLineWithdrawalIncomeVerificationPage,
  CreditLimitRecalculationIncomeVerificationPage,
  HirePurchaseIncomeVerificationPage,
  SmallLoanIncomeVerificationPage,
  // Spouse consent pages
  HirePurchaseSpouseConsentPage,
  SmallLoanSpouseConsentPage,
  CreditLineSpouseConsentPage,
  CreditLimitRecalculationSpouseConsentPage,
  CreditLineWithdrawalSpouseConsentPage,
  // Signing pages
  HirePurchaseSigningPage,
  SmallLoanSigningPage,
  CreditLineSigningPage,
  IncomeInsuranceSigningPage,
  // Success pages
  HirePurchaseSuccessPage,
  SmallLoanSuccessPage,
  CreditLineSuccessPage,
  IncomeInsuranceSuccessPage,
  // Reject pages
  HirePurchaseRejectPage,
  SmallLoanRejectPage,
  CreditLineRejectPage,
  IncomeInsuranceRejectPage,
  // Pending pages
  HirePurchasePendingPage,
  SmallLoanPendingPage,
  CreditLinePendingPage,
  CreditLimitRecalculationPendingPage,
  CreditLineWithdrawalPendingPage,
  // Error pages
  ErrorPage,
  // Short link handler page
  ShortLinkHandlerPage,
  // EParaksts Login page
  EParakstsLoginPage,
  // Offer pages (Hire Purchase)
  OfferNewsletterPage,
  OfferCreditAccountPage,
  OfferCreditAccountWithdrawalPage,
  OfferIncomeInsurancePage,
  // Offer pages (Small Loan)
  SmallLoanOfferNewsletterPage,
  SmallLoanOfferCreditAccountPage,
  SmallLoanOfferCreditAccountWithdrawalPage,
  SmallLoanOfferIncomeInsurancePage,

  // Direct payment product pages
  DirectPaymentRootPage,
  DirectPaymentFinalPage,
  DirectPaymentPage,
} = pages;

const sharedRoutes = [
  {
    path: ERROR_PAGE_PATH,
    element: <ErrorPage />,
  },
  {
    path: UNKNOWN_PAGE_ROUTE_PATH,
    element: <Redirect to={`../${ERROR_PAGE_PATH}`} />,
  },
];

const useSentryRoutes = wrapUseRoutesV6(useRoutes);

const AppRouter = memo(() => {
  const shouldNavigateToSchedulePage = useShouldShowSchedulePage();

  return useSentryRoutes([
    {
      path: `${AppRoutePaths.SHORT_LINK}/:${AppRouterParams.shortReferenceCode}`,
      element: <ShortLinkHandlerPage />,
    },
    {
      path: EPARAKSTS_LOGIN_PAGE_PATH,
      element: <EParakstsLoginPage />,
    },
    {
      path: AppRoutePaths.HIRE_PURCHASE,
      element: <HirePurchaseRootPage />,
      children: [
        {
          path: ROOT_PAGE_PATH,
          element: (
            <>
              <DefaultLoader />
              <Redirect
                to={
                  shouldNavigateToSchedulePage
                    ? HirePurchaseRoutePaths.SCHEDULE
                    : HirePurchaseRoutePaths.CHECKOUT
                }
              />
            </>
          ),
        },
        {
          path: HirePurchaseRoutePaths.SCHEDULE,
          element: (
            <PageUrlCheckRender>
              <HirePurchaseSchedulePage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.CHECKOUT,
          element: (
            <PageUrlCheckRender>
              <HirePurchaseCheckoutPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.LOGIN,
          element: (
            <PageUrlCheckRender>
              <HirePurchaseLoginPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.CONTACT,
          element: (
            <PageUrlCheckRender>
              <HirePurchaseContactPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.PURPOSE_OF_LOAN,
          element: (
            <PageUrlCheckRender>
              <HirePurchasePurposeOfLoanPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.CONTACT_EXTRA,
          element: (
            <PageUrlCheckRender>
              <HirePurchaseContactExtraPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.SPOUSE,
          element: <HirePurchaseSpouseConsentPage />,
        },
        {
          path: HirePurchaseRoutePaths.ACCOUNT_SCORING,
          element: (
            <PageUrlCheckRender>
              <HirePurchaseAccountScoringPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.INCOME_VERIFICATION,
          element: (
            <PageUrlCheckRender>
              <HirePurchaseIncomeVerificationPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.EMTA_CONSENT,
          element: (
            <PageUrlCheckRender>
              <HirePurchaseEmtaConsentPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.SIGNING,
          element: (
            <PageUrlCheckRender>
              <HirePurchaseSigningPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.PENDING,
          element: (
            <PageUrlCheckRender>
              <HirePurchasePendingPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.OFFER_NEWSLETTER,
          element: (
            <PageUrlCheckRender>
              <OfferNewsletterPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.OFFER_CREDIT_ACCOUNT_WITHDRAWAL,
          element: (
            <PageUrlCheckRender>
              <OfferCreditAccountWithdrawalPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.OFFER_CREDIT_ACCOUNT,
          element: (
            <PageUrlCheckRender>
              <OfferCreditAccountPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.OFFER_INCOME_INSURANCE,
          element: (
            <PageUrlCheckRender>
              <OfferIncomeInsurancePage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.SUCCESS,
          element: (
            <PageUrlCheckRender>
              <HirePurchaseSuccessPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: HirePurchaseRoutePaths.DIRECT_PAYMENT,
          element: <Redirect to={`/${AppRoutePaths.DIRECT_PAYMENT}`} />,
        },
        {
          path: HirePurchaseRoutePaths.AFTER_DIRECT_PAYMENT,
          element: (
            <Redirect
              to={`/${AppRoutePaths.DIRECT_PAYMENT}/${DirectPaymentRoutePaths.FINAL}`}
            />
          ),
        },
        {
          path: HirePurchaseRoutePaths.AFTER_DIRECT_CARD_PAYMENT,
          element: (
            <Redirect
              to={`/${AppRoutePaths.DIRECT_PAYMENT}/${DirectPaymentRoutePaths.FINAL_CARD}`}
            />
          ),
        },
        {
          path: HirePurchaseRoutePaths.REJECT,
          element: (
            <PageUrlCheckRender>
              <HirePurchaseRejectPage />
            </PageUrlCheckRender>
          ),
        },
        ...sharedRoutes,
      ],
    },
    {
      path: AppRoutePaths.DIRECT_PAYMENT,
      element: <DirectPaymentRootPage />,
      children: [
        {
          path: ROOT_PAGE_PATH,
          element: <DirectPaymentPage />,
        },
        {
          path: DirectPaymentRoutePaths.FINAL,
          element: <DirectPaymentFinalPage />,
        },
        {
          path: DirectPaymentRoutePaths.FINAL_CARD,
          element: <DirectPaymentFinalPage />,
        },
        ...sharedRoutes,
      ],
    },
    {
      path: AppRoutePaths.SMALL_LOAN,
      element: <SmallLoanRootPage />,
      children: [
        {
          path: ROOT_PAGE_PATH,
          element: (
            <>
              <DefaultLoader />
              <Redirect to={SmallLoanRoutePaths.CHECKOUT} />
            </>
          ),
        },
        {
          path: SmallLoanRoutePaths.CHECKOUT,
          element: (
            <PageUrlCheckRender>
              <SmallLoanCheckoutPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: SmallLoanRoutePaths.LOGIN,
          element: (
            <PageUrlCheckRender>
              <SmallLoanLoginPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: SmallLoanRoutePaths.CONTACT,
          element: (
            <PageUrlCheckRender>
              <SmallLoanContactPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: SmallLoanRoutePaths.PURPOSE_OF_LOAN,
          element: (
            <PageUrlCheckRender>
              <SmallLoanPurposeOfLoanPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: SmallLoanRoutePaths.CONTACT_EXTRA,
          element: (
            <PageUrlCheckRender>
              <SmallLoanContactExtraPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: SmallLoanRoutePaths.SPOUSE,
          element: <SmallLoanSpouseConsentPage />,
        },
        {
          path: SmallLoanRoutePaths.ACCOUNT_SCORING,
          element: (
            <PageUrlCheckRender>
              <SmallLoanAccountScoringPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: SmallLoanRoutePaths.INCOME_VERIFICATION,
          element: (
            <PageUrlCheckRender>
              <SmallLoanIncomeVerificationPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: SmallLoanRoutePaths.EMTA_CONSENT,
          element: (
            <PageUrlCheckRender>
              <SmallLoanEmtaConsentPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: SmallLoanRoutePaths.SIGNING,
          element: (
            <PageUrlCheckRender>
              <SmallLoanSigningPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: SmallLoanRoutePaths.PENDING,
          element: (
            <PageUrlCheckRender>
              <SmallLoanPendingPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: SmallLoanRoutePaths.OFFER_NEWSLETTER,
          element: (
            <PageUrlCheckRender>
              <SmallLoanOfferNewsletterPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: SmallLoanRoutePaths.OFFER_CREDIT_ACCOUNT_WITHDRAWAL,
          element: (
            <PageUrlCheckRender>
              <SmallLoanOfferCreditAccountWithdrawalPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: SmallLoanRoutePaths.OFFER_CREDIT_ACCOUNT,
          element: (
            <PageUrlCheckRender>
              <SmallLoanOfferCreditAccountPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: SmallLoanRoutePaths.OFFER_INCOME_INSURANCE,
          element: (
            <PageUrlCheckRender>
              <SmallLoanOfferIncomeInsurancePage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: SmallLoanRoutePaths.SUCCESS,
          element: (
            <PageUrlCheckRender>
              <SmallLoanSuccessPage />
            </PageUrlCheckRender>
          ),
        },

        {
          path: SmallLoanRoutePaths.REJECT,
          element: (
            <PageUrlCheckRender>
              <SmallLoanRejectPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: UNKNOWN_PAGE_ROUTE_PATH,
          element: <Redirect to={`../${ERROR_PAGE_PATH}`} />,
        },
        ...sharedRoutes,
      ],
    },
    {
      path: AppRoutePaths.CREDIT_LINE,
      element: <CreditLineRootPage />,
      children: [
        {
          path: ROOT_PAGE_PATH,
          element: <Redirect to={CreditLineRoutePaths.CHECKOUT} />,
        },
        {
          path: CreditLineRoutePaths.CHECKOUT,
          element: (
            <CreditLineRouteWithPageUrlCheck>
              <CreditLineCheckoutPage />
            </CreditLineRouteWithPageUrlCheck>
          ),
        },
        {
          path: CreditLineRoutePaths.LOGIN,
          element: (
            <CreditLineRouteWithPageUrlCheck>
              <CreditLineLoginPage />
            </CreditLineRouteWithPageUrlCheck>
          ),
        },
        {
          path: CreditLineRoutePaths.CONTACT,
          element: (
            <CreditLineRouteWithPageUrlCheck>
              <CreditLineContactPage />
            </CreditLineRouteWithPageUrlCheck>
          ),
        },
        {
          path: CreditLineRoutePaths.PURPOSE_OF_LOAN,
          element: (
            <CreditLineRouteWithPageUrlCheck>
              <CreditLinePurposeOfLoanPage />
            </CreditLineRouteWithPageUrlCheck>
          ),
        },
        {
          path: CreditLineRoutePaths.CONTACT_EXTRA,
          element: (
            <CreditLineRouteWithPageUrlCheck>
              <CreditLineContactExtraPage />
            </CreditLineRouteWithPageUrlCheck>
          ),
        },
        {
          path: CreditLineRoutePaths.SPOUSE,
          element: <CreditLineSpouseConsentPage />,
        },
        {
          path: CreditLineRoutePaths.ACCOUNT_SCORING,
          element: (
            <CreditLineRouteWithPageUrlCheck>
              <CreditLineAccountScoringPage />
            </CreditLineRouteWithPageUrlCheck>
          ),
        },
        {
          path: CreditLineRoutePaths.INCOME_VERIFICATION,
          element: (
            <CreditLineRouteWithPageUrlCheck>
              <CreditLineIncomeVerificationPage />
            </CreditLineRouteWithPageUrlCheck>
          ),
        },
        {
          path: CreditLineRoutePaths.EMTA_CONSENT,
          element: (
            <CreditLineRouteWithPageUrlCheck>
              <CreditLineEmtaConsentPage />
            </CreditLineRouteWithPageUrlCheck>
          ),
        },
        {
          path: CreditLineRoutePaths.SIGNING,
          element: (
            <CreditLineRouteWithPageUrlCheck>
              <CreditLineSigningPage />
            </CreditLineRouteWithPageUrlCheck>
          ),
        },
        {
          path: CreditLineRoutePaths.PENDING,
          element: (
            <CreditLineRouteWithPageUrlCheck>
              <CreditLinePendingPage />
            </CreditLineRouteWithPageUrlCheck>
          ),
        },
        {
          path: CreditLineRoutePaths.SUCCESS,
          element: (
            <CreditLineRouteWithPageUrlCheck>
              <CreditLineSuccessPage />
            </CreditLineRouteWithPageUrlCheck>
          ),
        },
        {
          path: CreditLineRoutePaths.REJECT,
          element: (
            <CreditLineRouteWithPageUrlCheck>
              <CreditLineRejectPage />
            </CreditLineRouteWithPageUrlCheck>
          ),
        },
        ...sharedRoutes,
      ],
    },
    {
      path: AppRoutePaths.CREDIT_LIMIT_RECALCULATION,
      element: <CreditLimitRecalculationRootPage />,
      children: [
        {
          path: ROOT_PAGE_PATH,
          element: (
            <>
              <DefaultLoader />
              <Redirect to={CreditLimitRecalculationRoutePaths.CHECKOUT} />
            </>
          ),
        },
        {
          path: CreditLimitRecalculationRoutePaths.CHECKOUT,
          element: (
            <PageUrlCheckRender>
              <CreditLimitRecalculationCheckoutPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLimitRecalculationRoutePaths.LOGIN,
          element: (
            <PageUrlCheckRender>
              <CreditLimitRecalculationLoginPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLimitRecalculationRoutePaths.CONTACT,
          element: (
            <PageUrlCheckRender>
              <CreditLimitRecalculationContactPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLimitRecalculationRoutePaths.PURPOSE_OF_LOAN,
          element: (
            <PageUrlCheckRender>
              <CreditLimitRecalculationPurposeOfLoanPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLimitRecalculationRoutePaths.CONTACT_EXTRA,
          element: (
            <PageUrlCheckRender>
              <CreditLimitRecalculationContactExtraPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLimitRecalculationRoutePaths.INCOME_VERIFICATION,
          element: (
            <PageUrlCheckRender>
              <CreditLimitRecalculationIncomeVerificationPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLimitRecalculationRoutePaths.SPOUSE,
          element: (
            <PageUrlCheckRender>
              <CreditLimitRecalculationSpouseConsentPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLimitRecalculationRoutePaths.ACCOUNT_SCORING,
          element: (
            <PageUrlCheckRender>
              <CreditLimitRecalculationAccountScoringPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLimitRecalculationRoutePaths.EMTA_CONSENT,
          element: (
            <PageUrlCheckRender>
              <CreditLimitRecalculationEmtaConsentPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLimitRecalculationRoutePaths.PENDING,
          element: (
            <PageUrlCheckRender>
              <CreditLimitRecalculationPendingPage />
            </PageUrlCheckRender>
          ),
        },
        ...sharedRoutes,
      ],
    },
    {
      path: AppRoutePaths.CREDIT_LINE_WITHDRAWAL,
      element: <CreditLineWithdrawalRootPage />,
      children: [
        {
          path: ROOT_PAGE_PATH,
          element: (
            <>
              <DefaultLoader />
              <Redirect to={CreditLineWithdrawalRoutePaths.CHECKOUT} />
            </>
          ),
        },
        {
          path: CreditLineWithdrawalRoutePaths.CHECKOUT,
          element: (
            <PageUrlCheckRender>
              <CreditLineWithdrawalCheckoutPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLineWithdrawalRoutePaths.LOGIN,
          element: (
            <PageUrlCheckRender>
              <CreditLineWithdrawalLoginPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLineWithdrawalRoutePaths.CONTACT,
          element: (
            <PageUrlCheckRender>
              <CreditLineWithdrawalContactPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLineWithdrawalRoutePaths.PURPOSE_OF_LOAN,
          element: (
            <PageUrlCheckedRoute
              component={CreditLineWithdrawalPurposeOfLoanPage}
            />
          ),
        },
        {
          path: CreditLineWithdrawalRoutePaths.CONTACT_EXTRA,
          element: (
            <PageUrlCheckRender>
              <CreditLineWithdrawalContactExtraPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLineWithdrawalRoutePaths.SPOUSE,
          element: <CreditLineWithdrawalSpouseConsentPage />,
        },
        {
          path: CreditLineWithdrawalRoutePaths.ACCOUNT_SCORING,
          element: (
            <PageUrlCheckRender>
              <CreditLineWithdrawalAccountScoringPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLineWithdrawalRoutePaths.INCOME_VERIFICATION,
          element: (
            <PageUrlCheckRender>
              <CreditLineWithdrawalIncomeVerificationPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLineWithdrawalRoutePaths.EMTA_CONSENT,
          element: (
            <PageUrlCheckRender>
              <CreditLineWithdrawalEmtaConsentPage />
            </PageUrlCheckRender>
          ),
        },
        {
          path: CreditLineWithdrawalRoutePaths.PENDING,
          element: (
            <PageUrlCheckRender>
              <CreditLineWithdrawalPendingPage />
            </PageUrlCheckRender>
          ),
        },
        ...sharedRoutes,
      ],
    },
    {
      path: AppRoutePaths.INCOME_INSURANCE,
      element: <IncomeInsuranceRootPage />,
      children: [
        {
          path: ROOT_PAGE_PATH,
          element: (
            <AuthRedirect
              to={IncomeInsuranceRoutePaths.SIGNING}
              auth={IncomeInsuranceRoutePaths.LOGIN}
            />
          ),
        },
        {
          path: IncomeInsuranceRoutePaths.LOGIN,
          element: (
            <AuthInsuranceHashPageUrlCheckRender>
              <IncomeInsuranceLoginPage />
            </AuthInsuranceHashPageUrlCheckRender>
          ),
        },
        {
          path: IncomeInsuranceRoutePaths.SIGNING,
          element: (
            <AuthInsuranceHashPageUrlCheckRender>
              <IncomeInsuranceSigningPage />
            </AuthInsuranceHashPageUrlCheckRender>
          ),
        },
        {
          path: IncomeInsuranceRoutePaths.SUCCESS,
          element: (
            <AuthInsuranceHashPageUrlCheckRender>
              <IncomeInsuranceSuccessPage />
            </AuthInsuranceHashPageUrlCheckRender>
          ),
        },
        {
          path: IncomeInsuranceRoutePaths.REJECT,
          element: (
            <AuthInsuranceHashPageUrlCheckRender>
              <IncomeInsuranceRejectPage />
            </AuthInsuranceHashPageUrlCheckRender>
          ),
        },
      ],
      ...sharedRoutes,
    },
    ...sharedRoutes,
  ]);
});

export default AppRouter;
